import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import { api, ApiError } from '../lib/api.js';
import type { Selection } from '../App.js';
import {
  alsoAsChoices,
  COMPOSER_KINDS as KINDS,
  isVideoKind,
  pickAlsoAs,
  type ComposerKindId,
} from '../lib/composerKinds.js';
import { composerKindFor, suggestedAlsoAs, type StudioHandoff } from '../lib/studioProject.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { EmojiPicker } from './EmojiPicker.js';
import { Icon } from './Icons.js';

/**
 * The composer: the same building blocks Facebook's own publisher exposes —
 * text, photos, video, stories, a first comment and scheduling.
 */
export function ComposerForm({
  onSelect,
  handoff,
}: {
  onSelect: (selection: Selection) => void;
  /**
   * Text and media handed off from elsewhere — the Studio sending a project's
   * words and files straight into this form. Used only as the initial value:
   * the composer then owns them like anything typed or uploaded here, and the
   * caller is told once it has been consumed so it does not reappear on a
   * later visit to this tab. The project id rides along so the saved
   * selection can name where it came from.
   */
  handoff?: StudioHandoff;
}) {
  const { t } = useI18n();
  const initialAssets = handoff?.assets;

  // The post type follows what arrives: a video opens on the video type, one
  // picture on PHOTO, several on MULTI_PHOTO, and words alone on TEXT. The
  // attachment is visible the moment this form opens instead of hiding behind
  // a post-type switch nobody was told to flip.
  const [kind, setKind] = useState<ComposerKindId>(() =>
    initialAssets ? composerKindFor(initialAssets, handoff?.aspect) : 'TEXT',
  );
  // "Also publish as": a suggested Reel opens with the Story box ticked, and
  // nothing is sent unless the operator leaves it ticked.
  const [alsoTicked, setAlsoTicked] = useState(() =>
    suggestedAlsoAs(initialAssets ? composerKindFor(initialAssets, handoff?.aspect) : 'TEXT'),
  );
  const [message, setMessage] = useState(handoff?.message ?? '');
  const [link, setLink] = useState('');
  const [firstComment, setFirstComment] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [assets, setAssets] = useState<Array<{ id: string; filename: string }>>(
    () => initialAssets ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const messageInput = useRef<HTMLTextAreaElement>(null);
  const firstCommentInput = useRef<HTMLInputElement>(null);

  const spec = KINDS.find((entry) => entry.id === kind);
  const isStory = kind === 'PHOTO_STORY' || kind === 'VIDEO_STORY';
  const alsoChoice = alsoAsChoices(kind, assets.length, Boolean(scheduleAt));
  const alsoAs = pickAlsoAs(alsoTicked, alsoChoice);

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    if (files.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      const uploaded: Array<{ id: string; filename: string }> = [];
      for (const file of files) {
        const result = await api.uploadMedia(file);
        uploaded.push({ id: result.asset.id, filename: result.asset.filename });
      }
      setAssets((current) => [...current, ...uploaded].slice(0, spec?.media ?? 0));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('composer.uploadFailed'));
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const composition = await api.createComposition({
        kind,
        message: message || undefined,
        link: link || undefined,
        mediaAssetIds: assets.map((asset) => asset.id),
        firstComment: firstComment || undefined,
        scheduledPublishTime: scheduleAt
          ? Math.floor(new Date(scheduleAt).getTime() / 1000)
          : undefined,
      });

      onSelect({
        sourceKind: 'COMPOSITION',
        compositionId: composition.composition.id,
        label: t(`composer.kind.${kind}` as TranslationKey),
        preview: message.slice(0, 120) || assets.map((a) => a.filename).join(', '),
        ...(alsoAs.length > 0 ? { alsoAs } : {}),
        ...(handoff?.projectId
          ? {
              projectId: handoff.projectId,
              // Only the project's own files — anything uploaded here is not its output.
              projectMediaIds: assets
                .map((asset) => asset.id)
                .filter((id) => initialAssets?.some((entry) => entry.id === id)),
            }
          : {}),
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('composer.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const mediaLabel =
    kind === 'MULTI_PHOTO'
      ? t('composer.photos')
      : isVideoKind(kind)
        ? t('composer.videoFile')
        : t('composer.photo');

  return (
    <form className="card composer" onSubmit={submit}>
      <label htmlFor="kind">{t('composer.postType')}</label>
      <select
        id="kind"
        value={kind}
        onChange={(event) => {
          setKind(event.target.value as ComposerKindId);
          setAssets([]);
        }}
      >
        {KINDS.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {t(`composer.kind.${entry.id}` as TranslationKey)}
          </option>
        ))}
      </select>

      {!isStory && (
        <>
          <label htmlFor="message">{t('composer.text')}</label>
          <textarea
            id="message"
            ref={messageInput}
            rows={5}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('composer.textPlaceholder')}
          />
          <EmojiPicker target={messageInput} value={message} onChange={setMessage} />
        </>
      )}

      {kind === 'LINK' && (
        <>
          <label htmlFor="link">{t('composer.link')}</label>
          <input
            id="link"
            type="url"
            className="ltr"
            value={link}
            onChange={(event) => setLink(event.target.value)}
            placeholder="https://…"
            required
          />
        </>
      )}

      {(spec?.media ?? 0) > 0 && (
        <>
          <label htmlFor="media">{mediaLabel}</label>
          <input
            id="media"
            type="file"
            multiple={kind === 'MULTI_PHOTO'}
            accept={isVideoKind(kind) ? 'video/mp4,video/quicktime' : 'image/*'}
            onChange={upload}
          />
          {assets.length > 0 && (
            <ul className="asset-list">
              {assets.map((asset) => (
                <li key={asset.id}>
                  <span className="small">{asset.filename}</span>
                  <button
                    type="button"
                    className="ghost small"
                    onClick={() => setAssets((current) => current.filter((a) => a.id !== asset.id))}
                  >
                    {t('common.remove')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {kind === 'REEL' && <p className="muted small">{t('composer.reelHint')}</p>}
        </>
      )}

      {alsoChoice.visible && (
        <fieldset className="also-as">
          <legend>{t('composer.alsoAs')}</legend>
          <label className="check-row">
            <input
              type="checkbox"
              checked={alsoTicked.story && alsoChoice.story}
              disabled={!alsoChoice.story}
              onChange={(event) => setAlsoTicked((current) => ({ ...current, story: event.target.checked }))}
            />
            {t('composer.alsoAs.story')}
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={alsoTicked.reel && alsoChoice.reel}
              disabled={!alsoChoice.reel}
              onChange={(event) => setAlsoTicked((current) => ({ ...current, reel: event.target.checked }))}
            />
            {t('composer.alsoAs.reel')}
          </label>
          <p className="muted tiny">{t('composer.alsoAs.hint')}</p>
        </fieldset>
      )}

      {!isStory && (
        <>
          <label htmlFor="first-comment">{t('composer.firstComment')}</label>
          <input
            id="first-comment"
            ref={firstCommentInput}
            type="text"
            value={firstComment}
            onChange={(event) => setFirstComment(event.target.value)}
            placeholder={t('composer.firstCommentPlaceholder')}
          />
          <EmojiPicker target={firstCommentInput} value={firstComment} onChange={setFirstComment} />

          {/*
            An optional date field was easy to miss entirely. An explicit choice
            makes it obvious that scheduling exists, and that the default is to
            publish immediately.
          */}
          <div className="schedule-box">
            <div className="schedule-choice">
              <label>
                <input
                  type="radio"
                  name="when"
                  checked={!scheduleAt}
                  onChange={() => setScheduleAt('')}
                />
                {t('composer.publishNow')}
              </label>
              <label>
                <input
                  type="radio"
                  name="when"
                  checked={Boolean(scheduleAt)}
                  onChange={() => setScheduleAt(defaultScheduleValue())}
                />
                {t('composer.scheduleLater')}
              </label>
            </div>

            {scheduleAt && (
              <>
                <input
                  id="schedule"
                  type="datetime-local"
                  className="ltr"
                  value={scheduleAt}
                  min={toLocalInput(new Date(Date.now() + 11 * 60_000))}
                  max={toLocalInput(new Date(Date.now() + 74 * 24 * 60 * 60_000))}
                  onChange={(event) => setScheduleAt(event.target.value)}
                />
                <ScheduleHint value={scheduleAt} />
              </>
            )}
          </div>
        </>
      )}

      {isStory && <div className="notice">{t('composer.storyNotice')}</div>}

      {error && <p className="error small">{error}</p>}

      <button type="submit" className="primary" disabled={busy}>
        <Icon.check />
        {busy ? t('composer.saving') : t('composer.submit')}
      </button>
    </form>
  );
}

/**
 * Tell the operator whether the chosen time is acceptable, rather than letting
 * Meta reject it after they press Publish. Meta's window is 10 minutes to
 * 75 days ahead.
 */
function ScheduleHint({ value }: { value: string }) {
  const { t, locale } = useI18n();
  const when = new Date(value).getTime();

  if (Number.isNaN(when)) return <p className="error small">{t('composer.scheduleInvalid')}</p>;

  const minutes = Math.round((when - Date.now()) / 60_000);
  if (minutes < 10) return <p className="warn small">{t('composer.scheduleTooSoon')}</p>;
  if (minutes > 75 * 24 * 60) return <p className="warn small">{t('composer.scheduleTooLate')}</p>;

  // Intl.RelativeTimeFormat gives a natural phrase in both languages instead of
  // a hand-assembled string that would need its own plural rules per language.
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const phrase =
    minutes < 60
      ? rtf.format(minutes, 'minute')
      : minutes < 48 * 60
        ? rtf.format(Math.round(minutes / 60), 'hour')
        : rtf.format(Math.round(minutes / (60 * 24)), 'day');

  return <p className="muted small">{t('composer.scheduleIn', { when: phrase })}</p>;
}

/**
 * `datetime-local` needs a local-time string, not an ISO/UTC one — passing UTC
 * silently shifts the displayed time by the timezone offset.
 */
function toLocalInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/** Default to an hour out: comfortably inside Meta's 10-minute floor. */
function defaultScheduleValue(): string {
  return toLocalInput(new Date(Date.now() + 60 * 60_000));
}
